import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Input, Space, Row, Col, Radio, Form, message, Upload, Table, Tooltip, InputNumber, Button, Popconfirm } from 'antd';
import { CloudUploadOutlined, InboxOutlined, EditOutlined, SaveOutlined, DownloadOutlined, QuestionCircleOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../store/auth/selectors';
import './PoaSostenibilidadMVD.css';
import config from '../../config';

import {
    uploadPoasSostenibilidadSoportes,
    getPoasSostenibilidadSoportes,
    deletePoaSostenibilidadSoporte,
    updatePoasSostenibilidadValue
} from "../../services/sostenibilidad/poaSosteibilidad";



const PoaSostenibilidadMVD = ({ isVisible, onClose, record, codigoInterno, updateLocalPoaValue }) => {

    // Variables derivadas
    const user = useSelector((state) => state.auth.user);
    const userLogin = user.username;
    const appName = 'PoaSostenibilidadMVD';

    // Permisos
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    const permissionDelete = permissions.priv_delete === 'Y';
    const permissionUpdate = permissions.priv_update === 'Y';
    const permissionInsert = permissions.priv_insert === 'Y';
    const permissionExport = permissions.priv_export === 'Y';
    const permissionPrint = permissions.priv_print === 'Y';

    const [isFirstModalVisible, setIsFirstModalVisible] = useState(isVisible);
    const [modalSoportesVisible, setModalSoportesVisible] = useState(false);

    const [hover, setHover] = useState(false);
    const [loading, setLoading] = useState(true);



    //Editar avance
    const [ejecutados, setEjecutados] = useState(record.noDeEjecutados || 0); // ✅ Estado local para "Ejecutados"
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ✅ Función para actualizar el campo "Ejecutados"
    const handleSaveEjecutados = async () => {
        if (ejecutados > record.noDeActividades) {
            message.error("❌ El número de actividades ejecutadas no puede ser mayor al número de actividades proyectadas.");
            return;
        }

        try {
            // Aquí puedes llamar a tu servicio de actualización en la base de datos
            await updatePoasSostenibilidadValue(codigoInterno, record.key, { noDeEjecutados: ejecutados });
            message.success("✅ Avance actualizado correctamente");

            updateLocalPoaValue(record.key, ejecutados);

            setIsEditing(false);
        } catch (error) {
            message.error("❌ Error al actualizar el valor");
        }
    };

    //soportes
    const [fileList, setFileList] = useState([]);
    const [soportes, setSoportes] = useState([]);
    const keyActividad = record.key;
    const allowedExtensions = ['jpeg', 'jpg', 'png', 'pdf', 'xls', 'xlsx', 'doc', 'docx'];


    useEffect(() => {

        const fetchSoportes = async () => {
            try {
                const response = await getPoasSostenibilidadSoportes(codigoInterno, keyActividad);

                if (!response || response.length === 0) {
                    console.warn("📢 No se encontraron soportes.");
                    setSoportes([]);  // ✅ Establece la tabla vacía en lugar de mostrar un error
                    return;
                }

                // 🟢 Transformar los datos para la tabla
                const mappedData = response.map((item, index) => ({
                    key: item.oid || index, // Clave única
                    documento: item.typeFile || "Sin título",
                    formato: Array.isArray(item.typeFile)
                        ? item.typeFile.map(file => file.split('.').pop()).join(", ")
                        : "N/A",

                    tipoSoporte: item.dataFile.resourceType || "No reportado",
                    usuario: item.userName || "Desconocido",
                    creacion: new Date(item.created).toLocaleDateString("es-ES"), // Formato de fecha
                    rutaImagen: item.rutaImagen, // Para descargar/ver
                    nameOrigin: item.dataFile.nameOrigin || "No reportado",
                }));

                setSoportes(mappedData);
            } catch (error) {

                console.warn("📢 No se encontraron soportes..");
            } finally {
                setLoading(false);
            }
        };

        fetchSoportes();
    }, [codigoInterno, keyActividad]);




    const sanitizeFileName = (fileName) => {
        return fileName
            .normalize("NFD") // ✅ Normalizar tildes y ñ
            .replace(/[\u0300-\u036f]/g, "") // ✅ Eliminar diacríticos
            .replace(/[^\w\sñÑáéíóúÁÉÍÓÚ.-]/g, "") // ✅ Mantener solo letras, números, espacios, guiones y puntos
            .trim(); // ✅ Eliminar espacios en los extremos
    };

    // 🟢 Función para manejar la subida de archivos
    const handleUpload = async () => {
        try {
            // ✅ Validar los campos del formulario antes de continuar
            const values = await form.validateFields();
            const { titulo, resourceType } = values;

            if (fileList.length === 0) {
                message.warning("📢 No se han seleccionado archivos.");
                return;
            }

            setIsSubmitting(true);

            // ✅ Mapear archivos reales (extraer `originFileObj`)
            const archivosReales = fileList.map(file => file.originFileObj || file);

            // ✅ Generar `dataFileArray` con información individual para cada archivo
            const dataFileArray = archivosReales.map((file) => ({
                key: record.key,
                title: record.title,
                tipoActividad: record.tipoActividad,
                actor: record.actor,
                mdv: record.mdv,
                titulo,
                resourceType,
                nameOrigin: sanitizeFileName(file.name) // ✅ Limpieza del nombre original
            }));

            // ✅ Generar `typeFile` con nombres únicos por archivo
            const typeFile = archivosReales.map((file, index) => {
                const ext = file.name.split('.').pop(); // Obtener la extensión
                return `${titulo}_${index + 1}.${ext}`; // 🔹 Agregar índice único
            });

            // ✅ Llamar al servicio con los datos correctos
            const response = await uploadPoasSostenibilidadSoportes(
                codigoInterno,
                record.key,
                user.username,
                archivosReales, // ✅ Enviar solo los archivos originales
                dataFileArray,  // ✅ Ahora `dataFileArray` es una lista con valores individuales
                typeFile        // ✅ Enviar nombres de archivos corregidos
            );

            // ✅ Evaluar la respuesta correctamente
            if (response && response.success) {


                message.success("✅ Soportes subidos correctamente");
                form.resetFields(); // ✅ Limpiar formulario
                setFileList([]); // ✅ Limpiar archivos inmediatamente

                // ✅ Agregar los nuevos soportes al estado sin recargar
                const newSoportes = archivosReales.map((file, index) => ({
                    key: Date.now() + index, // 🔹 Clave única temporal
                    documento: typeFile[index],
                    formato: typeFile[index].split('.').pop(),
                    tipoSoporte: resourceType,
                    usuario: user.username,
                    creacion: new Date().toLocaleDateString("es-ES"),
                    rutaImagen: response.imagenes[index]?.ruta_imagen || "", // ✅ Ruta correcta desde el backend
                    nameOrigin: sanitizeFileName(file.name),
                }));

                setSoportes(prevSoportes => [...newSoportes, ...prevSoportes]); // ✅ Añadir al inicio



            } else {
                message.error("❌ Hubo un problema al subir los soportes");
            }
        } catch (error) {
            console.error("❌ Error al subir archivos:", error);
            message.error("❌ Error al subir archivos");
        } finally {
            setIsSubmitting(false); // ✅ Asegurar que el botón se desbloquee siempre
        }
    };

    const handleCancelFile = () => {
        setModalSoportesVisible(false);
        setIsSubmitting(false);
        form.resetFields();
        setFileList([]); // ✅ Limpiar la lista de archivos

    };

    const handleCancel = () => {
        setIsFirstModalVisible(false);
        onClose();
    };

    const handleOkFile = () => {
        setModalSoportesVisible(true);
        setIsSubmitting(false);
        form.resetFields();
        setFileList([]); // ✅ Limpiar la lista de archivos
    };

    const handleDownload = async (path) => {
        if (!path) {
            message.error("No hay archivo disponible para descargar");
            return;
        }

        const fileUrl = `${config.baseUrl}/${path}`;

        // ✅ Crear un enlace y hacer clic automáticamente para descargar
        const link = document.createElement("a");
        link.href = fileUrl;
        link.setAttribute("download", ""); // Permitir descarga automática
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    const handleDeleteFile = async (rutaImagen) => {
        try {
            setLoading(true);

            // ✅ Llamar al servicio para eliminar el soporte
            const response = await deletePoaSostenibilidadSoporte(rutaImagen, codigoInterno, userLogin);

            if (response.success) {
                message.success("✅ Soporte eliminado correctamente");

                // ✅ Actualizar la lista de soportes eliminando el registro sin recargar toda la página
                setSoportes(prevSoportes => prevSoportes.filter(item => item.rutaImagen !== rutaImagen));
            } else {
                message.error("❌ No se pudo eliminar el soporte");
            }
        } catch (error) {
            message.error("❌ Error al eliminar el soporte");
        } finally {
            setLoading(false);
        }
    };

    const handleSeeFile = (path) => {
        if (!path) {
            message.error("No hay archivo disponible para visualizar");
            return;
        }

        const baseUrl = `${config.baseUrl}/${path}`;
        // ✅ Abrir el archivo en una nueva pestaña
        window.open(baseUrl, "_blank");
    };




    // ✅ Definir columnas de la tabla
    const columns = [
        {
            title: 'Opciones',
            key: 'action',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <Space size="middle">
                    {permissionExport && (

                        <Tooltip title="Descargar">
                            <Button
                                type="link"
                                icon={<DownloadOutlined style={{ fontSize: '1.1rem' }} />}
                                onClick={() => handleDownload(record.rutaImagen)} // ✅ Corregido: Se pasa la función correctamente
                            />
                        </Tooltip>
                    )}

                    {permissionExport && (
                        <Tooltip title="Visualizar">
                            <Button
                                type="link"
                                icon={<EyeOutlined style={{ fontSize: '1.1rem', color: '#08c' }} />}
                                onClick={() => handleSeeFile(record.rutaImagen)} // ✅ Corregido: Se pasa la función correctamente

                            />
                        </Tooltip>
                    )}
                    {permissionDelete && (
                        <Tooltip title="Eliminar">
                            <Popconfirm
                                title='Eliminar archivo'
                                description="¿Seguro que deseas eliminar este archivo?"
                                onConfirm={() => handleDeleteFile(record.rutaImagen)}
                                okText="Sí"
                                cancelText="No"
                                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                            >
                                <Button type="link" danger icon={<DeleteOutlined />} />

                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        {
            title: 'Documento',
            dataIndex: 'documento',
            key: 'documento',
            width: 300, // Aumenta el ancho de la columna
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={record.nameOrigin || "No reportado"}>
                    <span style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                        maxWidth: '100%' // Ahora ocupará todo el ancho disponible en la columna
                    }}>
                        {text}
                    </span>
                </Tooltip>
            )
        },
        {
            title: 'Formato', dataIndex: 'formato', key: 'formato', ellipsis: true, align: 'center',
            render: text => (
                <Tooltip title={text}>
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Tooltip>
            )
        },

        {
            title: 'Tipo de soporte', dataIndex: 'tipoSoporte', key: 'tipoSoporte', ellipsis: true,
            render: text => (
                <Tooltip title={text}>
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Tooltip>
            )
        },

        {
            title: 'Usuario', dataIndex: 'usuario', key: 'usuario', ellipsis: true, align: 'center',
            render: text => (
                <Tooltip title={text}>
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Tooltip>
            )
        },

        {
            title: 'Creación', dataIndex: 'creacion', key: 'creacion', ellipsis: true, align: 'center',
            render: text => (
                <Tooltip title={text}>
                    <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
                </Tooltip>
            )
        },
    ];

    return (
        <>
            <Modal
                title={<span style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', display: 'flex' }}>Medio de Verificación</span>}
                open={isFirstModalVisible}
                onCancel={handleCancel}
                footer={null}
                width="90%"
            >
                <div style={{
                    display: "flex",
                    flexDirection: "column", // Asegura que los elementos se coloquen en columna
                    alignItems: "center",  // Centrar horizontalmente
                    width: "100%",         // Ancho completo
                    gap: "20px"            // Espacio entre las secciones
                }}>

                    {/* 🟢 Primera sección: Descripción (Tabla de detalles) */}
                    <div style={{
                        display: "flex",
                        alignItems: "center", // Alinear elementos verticalmente
                        justifyContent: "space-between", // Espacio entre la tabla y el botón
                        width: "100%", // Asegurar que el contenedor use todo el ancho disponible
                        gap: "20px" // Espacio entre los elementos
                    }}>
                        {/* 🟢 Tabla (80%) */}
                        <div style={{ width: "80%" }}>
                            {record && (
                                <Descriptions bordered column={1} size="middle">
                                    <Descriptions.Item label="Subactividad">{record.title}</Descriptions.Item>
                                    <Descriptions.Item label="Medio de Verificación (MDV)">{record.mdv}</Descriptions.Item>
                                    <Descriptions.Item label="Tipo de Actividad">{record.tipoActividad}</Descriptions.Item>
                                    <Descriptions.Item label="Actor">{record.actor}</Descriptions.Item>
                                    <Descriptions.Item label="No. de Actividades">{record.noDeActividades}</Descriptions.Item>
                                    <Descriptions.Item label="Ejecutadas">
                                        <Space>
                                            {isEditing ? (
                                                <InputNumber
                                                    min={0}
                                                    value={ejecutados}
                                                    onChange={setEjecutados}
                                                    style={{ width: 80 }}
                                                />
                                            ) : (
                                                <span>{ejecutados}</span>
                                            )}
                                            {permissionUpdate && (
                                                <Button
                                                    icon={isEditing ? <SaveOutlined /> : <EditOutlined />}
                                                    onClick={isEditing ? handleSaveEjecutados : () => setIsEditing(true)}
                                                />
                                            )}
                                        </Space>
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                        </div>

                        {/* 🟢 Botón de carga (20%) */}

                        {permissionInsert && (

                            <div style={{ width: "20%", display: "flex", justifyContent: "center" }}>

                                <Button
                                    onClick={handleOkFile}
                                    type="primary"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '150px',
                                        width: '150px',
                                        border: '2px dashed #1890ff',
                                        background: hover ? '#e6f7ff' : '#f5f5f5',
                                        textAlign: 'center',
                                        padding: '10px',
                                        overflow: 'hidden',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.2',
                                        cursor: 'pointer',
                                        transition: 'background 0.3s ease'
                                    }}
                                    onMouseEnter={() => setHover(true)}
                                    onMouseLeave={() => setHover(false)}
                                >
                                    <CloudUploadOutlined style={{ fontSize: '40px', marginBottom: '10px', color: '#1677ff' }} />
                                    <p style={{ fontSize: '12px', color: '#888', margin: 0 }}>
                                        Haga clic para cargar el MVD.
                                    </p>
                                </Button>


                            </div>
                        )}
                    </div>



                    {/* 🟢 Segunda sección: Tabla de Soportes */}
                    <div style={{ width: "100%" }}>
                        <Table
                            columns={columns}
                            dataSource={soportes}
                            pagination={{ pageSize: 5 }}
                            loading={loading}
                            scroll={{ y: 'calc(100vh - 450px)', x: 'max-content' }}  // Se ajusta dinámicamente según la pantalla
                            size="small" // Reduce el padding de las celdas
                        />

                    </div>
                </div>
            </Modal>
            <Modal
                title={<span style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', display: 'flex' }}>Cargar medio de verificación</span>}
                open={modalSoportesVisible}
                onCancel={handleCancelFile}
                footer={null}
                width="50%"
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
            >

                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                    <Form
                        labelCol={{ span: 10 }}
                        wrapperCol={{ span: 50 }}
                        layout="vertical"
                        form={form}
                        initialValues={{
                            fecha: null,
                            titulo: '',
                            resourceType: null,
                        }}
                        onFinish={handleUpload}
                    //onValuesChange={handleFormChange}
                    >
                        <Form.Item
                            label="Nombre del soporte"
                            name="titulo"
                            rules={[
                                { required: true, message: 'Por favor, ingrese el Nombre del soporte' },
                                {
                                    //En el segundo capo pemitir espacios, colocar came case
                                    pattern: /^[0-9]{8}_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*$/,
                                    message: (
                                        <div>
                                            <p>El formato debe ser ddmmyyyy_Tipo_Ubicación. Ej: 18112023_Memoria_ElCharco</p>
                                            <ul>
                                                <li>Fecha en la que se realizó el espacio o el documento, en formato día-mes-año</li>
                                                <li>Guion bajo</li>
                                                <li>Nombre descriptivo corto del archivo</li>
                                                <li>Guion bajo</li>
                                                <li>Especificar el municipio o vereda en la que se realizó el espacio</li>
                                            </ul>
                                        </div>
                                    ),
                                },
                            ]}
                        >
                            <Input value={record.title} />

                        </Form.Item>
                        <Form.Item
                            label="Tipo de soporte"
                            name="resourceType"
                            rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}
                        >
                            <Radio.Group>
                                <Radio value="Memoria">Memoria</Radio>
                                <Radio value="Acta">Acta</Radio>
                                <Radio value="PPT">PPT</Radio>
                                <Radio value="Asistencia">Asistencia</Radio>
                                <Radio value="Informe">Informe</Radio>
                                <Radio value="Foto">Foto</Radio>
                                <Radio value="Grabación">Grabación</Radio>
                                <Radio value="Correo Aprobación">Correo Aprobación</Radio>
                                <Radio value="Documento técnico">Documento técnico</Radio>
                                <Radio value="Otro">Otro</Radio>
                            </Radio.Group>
                        </Form.Item>



                        <Form.Item
                            label="Carga de Soporte (Peso Maximo 30MB)"
                            name="soporte"
                            rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}
                            labelCol={{ span: 24 }}  // ✅ Hace que la etiqueta ocupe todo el ancho y no desplace el contenido
                            wrapperCol={{ span: 24 }}  // ✅ Centra el contenido dentro del Form.Item
                        >
                            <div className="upload-container">
                                <Row gutter={1}>
                                    <Col span={24}>
                                        <Space direction="vertical" size="large" style={{ width: '0%' }}>
                                            <Tooltip title="Subir Soporte">
                                                <Upload
                                                    multiple
                                                    fileList={fileList}
                                                    beforeUpload={(file) => {
                                                        const fileExt = file.name.split('.').pop().toLowerCase(); // Extrae la extensión en minúsculas

                                                        if (!allowedExtensions.includes(fileExt)) {
                                                            message.error(`❌ Extensión no permitida: ${fileExt.toUpperCase()}. 
                                                            Solo se permiten: ${allowedExtensions.join(', ').toUpperCase()}`);
                                                            return false; // ❌ No agregar el archivo a la lista
                                                        }

                                                        setFileList(prev => [...prev, file]); // ✅ Agregar solo si la extensión es válida
                                                        return false; // Evita la subida automática de Ant Design
                                                    }}
                                                    onRemove={(file) => {
                                                        setFileList(prev => prev.filter((item) => item.uid !== file.uid));
                                                    }}
                                                    className="upload-centered"
                                                >
                                                    <Button
                                                        icon={<CloudUploadOutlined />}
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: '150px',
                                                            width: '150px',
                                                            border: '2px dashed #1890ff',
                                                            background: hover ? '#e6f7ff' : '#f5f5f5',
                                                            textAlign: 'center',
                                                            padding: '70px',
                                                            overflow: 'hidden',
                                                            whiteSpace: 'normal',
                                                            lineHeight: '1.2',
                                                            cursor: 'pointer',
                                                            transition: 'background 0.3s ease'
                                                        }}
                                                    >
                                                        Seleccionar Archivos
                                                    </Button>
                                                </Upload>
                                            </Tooltip>
                                        </Space>
                                    </Col>
                                </Row>

                            </div>



                        </Form.Item>
                        <Form.Item>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>

                                <Button
                                    type="primary"
                                    onClick={handleCancelFile}
                                    style={{ marginRight: 8 }}
                                    danger
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() => form.submit()} // ✅ Esto activará la validación
                                    loading={isSubmitting} // ✅ Evita múltiples envíos mientras se procesa
                                >
                                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                                </Button>
                            </div>
                        </Form.Item>
                    </Form>
                </div>

            </Modal>
        </>

    );
};

export default PoaSostenibilidadMVD;


